import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, PawPrint } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PayPalTrust } from "@/components/PayPalTrust";

export default function Bundles() {
  const [packages, setPackages] = useState([]);
  const [email, setEmail] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => { api.get("/public/bundles").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  const buy = async (pkgId) => {
    if (!email) { toast.error("Please enter your email first"); return; }
    setLoadingId(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "bundle", ref_id: pkgId, email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoadingId(null);
    }
  };

  return (
    <div className="bg-white text-brand-ink min-h-screen">
      <PublicNav />
      <section className="pt-32 pb-12 px-6 lg:px-10 max-w-4xl mx-auto text-center">
        <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-5">Session Packs</div>
        <h1 className="font-display text-5xl sm:text-6xl">Practice more, save more</h1>
        <p className="mt-6 font-serif text-2xl text-brand-ink/70 max-w-2xl mx-auto">
          Bundle your 45-minute park sessions and share the practice with your pup all season long.
        </p>
        <div className="mt-8 max-w-sm mx-auto">
          <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Your email (for your pack)</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-2 w-full rounded-full border border-brand-line px-5 py-3 text-center focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="bundle-email" />
        </div>
      </section>

      <section className="pb-28 px-6 lg:px-10 max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
        {packages.map((p, idx) => (
          <div key={p.id} className={`rounded-[2rem] p-10 border ${idx === 1 ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-line"}`} data-testid={`bundle-card-${p.id}`}>
            <PawPrint className="w-7 h-7 text-brand-red" />
            <div className="mt-5 text-xs uppercase tracking-[0.3em] text-brand-red">{p.name}</div>
            <div className="mt-3 font-display text-6xl">${p.price}</div>
            <div className={`mt-2 text-sm ${idx === 1 ? "text-white/60" : "text-brand-ink/60"}`}>{p.per} · {p.save}</div>
            <ul className={`mt-6 space-y-2 text-sm ${idx === 1 ? "text-white/70" : "text-brand-ink/70"}`}>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-red" /> {p.sessions} × 45-minute sessions</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-red" /> Redeem at any park session</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-red" /> For you and your pet</li>
            </ul>
            <button onClick={() => buy(p.id)} disabled={loadingId === p.id} className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-medium transition-colors disabled:opacity-60 ${idx === 1 ? "bg-brand-red text-white hover:bg-brand-reddark" : "border border-brand-line hover:border-brand-red hover:text-brand-red"}`} data-testid={`bundle-buy-${p.id}`}>
              {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Buy {p.name}
            </button>
          </div>
        ))}
      </section>
      <PayPalTrust className="pb-20 px-6" />
      <Footer />
    </div>
  );
}
